import React, { useEffect, useState } from 'react';
import { Box, Text, Flex, Button } from '@chakra-ui/react';
import {
  ButtonIcon,
  FormFeedback,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models';
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form';
import FormFieldFigures from 'src/components/FormField/FormFieldFigures';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { DeleteFigureModal } from 'src/components/Submission/GenericFieldComponents/Figures/DeleteFigureModal';
import { ISubmissionFieldsResponse } from '@publishing-engine/submission-service/src/usecases/submission-fields/types';
import { getValidationTypedMessage } from 'src/components/FormField/common/errorMessages';
import { FieldLabel } from 'src/components/Field';

interface FiguresProps {
  config: any;
  submissionId: string;
  isFromEditModal?: boolean;
  submissionFields?: ISubmissionFieldsResponse;
}

export type IFiguresValue = {
  fileSet: {
    Figures?: IFiguresAndTablesFile;
  };
};

const Figures: React.FC<FiguresProps> = ({
  config,
  submissionId,
  isFromEditModal,
  submissionFields
}) => {
  const toast = useToast();
  const methods = useFormContext<IFiguresValue>();

  const { control } = methods;

  const fieldName = config.caseDataFields.main;

  // The title is the article type's label for the field, and SubmissionSidebar
  // scrolls to a section by looking that label up as a DOM id, so the label has
  // to stay the id of the heading.
  const fieldLabel = config.params.labels.main || 'Figures';
  const validations = config.params.validations;
  // A required array is given `minItems: 1` by the schema even when no minimum
  // is configured, so either one makes the field mandatory to the submitter.
  const isFieldRequired =
    Boolean(validations?.required) || (validations?.minItems ?? 0) > 0;

  // Figures are mandatory for some article types, and the schema reports that
  // as an error on the array itself rather than on a row. Nothing rendered it,
  // so a submission was blocked with no visible reason when the user had never
  // opened the table.
  const figuresErrors = methods.formState.errors?.fileSet?.Figures as
    | FieldError
    | FieldError[]
    | undefined;
  const figuresError =
    figuresErrors && !Array.isArray(figuresErrors) ? figuresErrors : undefined;

  // move useFieldArray to parent
  const { fields, append, remove, move, update } = useFieldArray({
    control,
    name: fieldName as any
  });

  const [selectedFigures, setSelectedFigures] = useState<
    Record<string, boolean>
  >({});
  const [deleteFigures, setDeleteFigures] = useState<boolean>(false);
  const [showFiguresForm, setShowFiguresForm] = useState(false);

  const deleteSelected = () => {
    const indexesToDelete = fields
      .map((field, index) => (selectedFigures[field.id] ? index : -1))
      .filter(i => i !== -1);

    if (indexesToDelete.length === 0) return;

    const allSelected = indexesToDelete.length === fields.length;

    if (allSelected) {
      remove([...indexesToDelete]);
      //append({ caption: '', file: null }); // add single empty row
      setSelectedFigures({});
      setDeleteFigures(false);
      toast({
        variant: 'success',
        title: `${indexesToDelete.length} figure${
          indexesToDelete.length > 1 ? 's' : ''
        } deleted successfully.`
      });
      setShowFiguresForm(false);
      return;
    }

    // delete only selected
    remove(indexesToDelete);

    // fix selected array size
    setSelectedFigures(() => {
      const updated: Record<string, boolean> = {};
      fields.slice(0, fields.length - indexesToDelete.length).forEach(f => {
        updated[f.id] = false;
      });
      return updated;
    });

    toast({
      variant: 'success',
      title: `${indexesToDelete.length} figure${
        indexesToDelete.length > 1 ? 's' : ''
      } deleted successfully.`
    });
    setDeleteFigures(false);
  };

  const toggleRow = (id: string) => {
    setSelectedFigures(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleSelectAll = () => {
    const allSelected = fields.every(f => selectedFigures[f.id]);
    const updated: Record<string, boolean> = {};

    fields.forEach(f => {
      updated[f.id] = !allSelected;
    });

    setSelectedFigures(updated);
  };

  useEffect(() => {
    const values = methods.getValues();

    if (
      Array.isArray(values?.fileSet?.Figures) &&
      values.fileSet.Figures.length > 0
    ) {
      setShowFiguresForm(true);
    }
  }, [methods]);

  return (
    <OutlineCard width="100%" padding={{ base: 3, md: 4 }}>
      <Flex flexDirection="column" width="100%" gap={{ base: 3, md: 4 }}>
        {/* Figures Section */}

        <Flex
          flexDirection="row"
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          width="100%"
          gap={{ base: 2, md: 0 }}
        >
          {/* Left Section */}
          <FieldLabel
            label={isFromEditModal ? `Update ${fieldLabel}` : fieldLabel}
            subText=""
            isRequired={isFieldRequired}
            textStyle={isFromEditModal ? 'headingSection' : 'headingItem'}
            helpText={config.params.labels.helpText}
          />

          {/* Right Section (Button aligned to right) */}
          <span>
            <ButtonIcon
              size="regular"
              ariaLabel="right-delete-button"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              onClick={() => {
                if (!Object.values(selectedFigures).some(x => x)) {
                  toast({
                    variant: 'attention',
                    title: 'No figure selected'
                  });
                  return;
                }
                setDeleteFigures(true);
              }}
              w={{ base: '44px', md: 'auto' }}
            />
          </span>
        </Flex>
        <Flex flexDirection="column" gap={{ base: 2, md: 1 }}>
          {!showFiguresForm ? (
            <Box textAlign={{ base: 'left', md: 'center' }} width="100%">
              <Text textStyle="context" mb={4}>
                Click on 'Add Figures' to add figures
              </Text>
              <Button
                variant="primary"
                onClick={() => setShowFiguresForm(true)}
                gap={2}
                width={{ base: '100%', sm: 'auto' }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Figures
              </Button>
            </Box>
          ) : (
            <Box textAlign={{ base: 'left', md: 'center' }}>
              <FormFieldFigures
                config={config}
                submissionId={submissionId}
                fields={fields}
                append={append}
                remove={remove}
                selected={selectedFigures}
                toggleRow={toggleRow}
                toggleSelectAll={toggleSelectAll}
                methods={methods}
                move={move}
                update={update}
                onLastFigureDeleted={() => {
                  setShowFiguresForm(false);
                  setSelectedFigures({});
                }}
                submissionFields={submissionFields}
              />
            </Box>
          )}
        </Flex>

        {figuresError && (
          <FormFeedback variant="error">
            {getValidationTypedMessage(
              fieldLabel,
              validations?.kind,
              validations,
              figuresError
            )}
          </FormFeedback>
        )}

        {deleteFigures && (
          <DeleteFigureModal
            onClose={() => setDeleteFigures(false)}
            isOpen={deleteFigures}
            onDelete={deleteSelected}
            selectedFigures={selectedFigures}
          />
        )}
      </Flex>
    </OutlineCard>
  );
};

export default Figures;
